import React from 'react';
import { ArrowLeft, Globe, Shield } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-white dark:bg-navy-950 font-sans text-slate-700 dark:text-slate-300">
      {/* Header */}
      <nav className="fixed top-0 w-full z-[100] bg-white/90 dark:bg-navy-950/90 backdrop-blur-md border-b border-neutral-100 dark:border-navy-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-navy-950 dark:hover:text-white transition-colors uppercase tracking-widest"
          >
            <ArrowLeft size={16} /> Voltar
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-navy-900 dark:bg-parliament-600 p-1.5 rounded-lg text-white">
              <Globe size={16} />
            </div>
            <span className="font-black text-sm tracking-tighter uppercase dark:text-white">Gabinete Digital</span>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-6">
        <article className="max-w-4xl mx-auto prose dark:prose-invert prose-slate prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tighter">
          
          <div className="mb-12 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-4 border border-emerald-100 dark:border-emerald-800">
              <Shield size={14} /> Documento Legal
            </span>
            <h1 className="text-4xl md:text-5xl mb-2 text-navy-950 dark:text-white">Política de Privacidade</h1>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Última atualização: 01/02/2026</p>
          </div>

          <div className="bg-neutral-50 dark:bg-navy-900/50 p-8 rounded-[32px] border border-neutral-200 dark:border-navy-800 space-y-8">
            <section>
              <h3>1. IDENTIFICAÇÃO DO CONTROLADOR DA PLATAFORMA</h3>
              <p>Esta plataforma é operada por:</p>
              <p><strong>ALMIRANTE DIGITAL LTDA</strong><br/>
              CNPJ: 59.120.082/0001-50<br/>
              Sede: Governador Valadares/MG<br/>
              E-mail para contato: agencia@dialetike.org</p>
              <p>Administrador responsável e Encarregado pelo Tratamento de Dados (DPO):<br/>
              <strong>Abel Henrique da Silva</strong></p>
            </section>

            <section>
              <h3>2. PAPEL DA EMPRESA NO TRATAMENTO DE DADOS</h3>
              <p>A plataforma Gabinete Digital é disponibilizada no modelo SaaS (Software as a Service).</p>
              <p>Os dados pessoais inseridos no sistema são cadastrados exclusivamente pelos usuários vinculados ao gabinete contratante (administradores, assessores ou responsáveis designados).</p>
              <p>A <strong>ALMIRANTE DIGITAL LTDA</strong>:</p>
              <ul>
                <li>não coleta dados diretamente de cidadãos;</li>
                <li>não realiza prospecção, enriquecimento ou cruzamento de dados;</li>
                <li>não utiliza os dados para fins próprios;</li>
                <li>não comercializa dados;</li>
                <li>não define as finalidades do tratamento;</li>
                <li>não realiza decisões automatizadas ou perfilamento para fins próprios.</li>
              </ul>
              <p>A empresa atua exclusivamente como <strong>operadora de dados</strong>, nos termos do art. 5º, VII da Lei nº 13.709/2018 (LGPD), limitando-se a:</p>
              <ul>
                <li>armazenar os dados em ambiente seguro;</li>
                <li>disponibilizar funcionalidades tecnológicas;</li>
                <li>manter a infraestrutura de banco de dados e autenticação;</li>
                <li>garantir medidas técnicas e administrativas de segurança.</li>
              </ul>
              <p>O <strong>Gabinete contratante</strong> é o <strong>Controlador dos dados</strong>, sendo o único responsável:</p>
              <ul>
                <li>pela coleta dos dados pessoais;</li>
                <li>pela definição da finalidade do tratamento;</li>
                <li>pela verificação da base legal aplicável;</li>
                <li>pela obtenção de consentimento quando necessário;</li>
                <li>pela legitimidade da manutenção de dados de natureza eleitoral ou política.</li>
              </ul>
              <p>A ALMIRANTE DIGITAL LTDA realiza tratamento de dados apenas conforme instruções documentadas do Controlador e dentro dos limites contratuais estabelecidos.</p>
            </section>

            <section>
              <h3>3. DADOS COLETADOS</h3>
              
              <h4>3.1 Dados de Usuários do Sistema (Equipe do Gabinete)</h4>
              <ul>
                <li>Nome completo</li>
                <li>E-mail</li>
                <li>Telefone (opcional)</li>
                <li>Avatar (URL da imagem)</li>
                <li>UUID</li>
                <li>Hash de senha (via Supabase Auth)</li>
                <li>Logs de login/logout</li>
                <li>Cargo/função e permissões</li>
                <li>Preferências internas</li>
              </ul>
              <p><strong>Finalidade:</strong> gestão de acesso, segurança da informação e organização interna do gabinete.</p>

              <h4>3.2 Dados de Cidadãos (CRM Parlamentar)</h4>
              <ul>
                <li>Nome completo</li>
                <li>Data de nascimento</li>
                <li>Profissão</li>
                <li>Telefone (WhatsApp)</li>
                <li>E-mail</li>
                <li>Endereço completo</li>
                <li>Coordenadas geográficas (Google Maps)</li>
                <li>Nível de apoio político</li>
                <li>Origem do contato</li>
                <li>Título de eleitor</li>
                <li>Zona e seção eleitoral</li>
                <li>Tags de segmentação</li>
                <li>Observações internas</li>
              </ul>
              <p><strong>Finalidade:</strong> Gestão de relacionamento parlamentar, atendimento institucional e organização de demandas.</p>

              <h4>3.3 Dados de Atendimentos</h4>
              <ul>
                <li>Descrição da demanda</li>
                <li>Histórico de comentários</li>
                <li>Status</li>
                <li>Anexos</li>
                <li>Canal de entrada</li>
              </ul>
              <p><strong>Finalidade:</strong> organização e acompanhamento de solicitações.</p>

              <h4>3.4 Dados de Documentos Oficiais</h4>
              <ul>
                <li>Nome de destinatários</li>
                <li>Assunto</li>
                <li>Conteúdo de ofícios</li>
                <li>Respostas recebidas</li>
              </ul>
              <p><strong>Finalidade:</strong> formalização de comunicações institucionais.</p>

              <h4>3.5 Dados Financeiros (Planos Pagos)</h4>
              <ul>
                <li>Nome/Razão Social</li>
                <li>CPF/CNPJ</li>
                <li>Endereço de cobrança</li>
                <li>Telefone</li>
                <li>Histórico de pagamentos</li>
                <li>ID de transação</li>
              </ul>
              <p>Os dados de cartão de crédito NÃO são armazenados na aplicação. O processamento é realizado via gateway <strong>ASAAS</strong>, restando apenas tokens de referência.</p>

              <h4>3.6 Dados Técnicos</h4>
              <ul>
                <li>Logs de auditoria</li>
                <li>Endereço IP</li>
                <li>User Agent</li>
                <li>Metadados de sessão</li>
              </ul>
              <p><strong>Finalidade:</strong> segurança, rastreabilidade e prevenção de fraudes.</p>
            </section>

            <section>
              <h3>4. BASES LEGAIS</h3>
              <p>O tratamento poderá se fundamentar em:</p>
              <ul>
                <li>Execução de contrato (art. 7º, V da LGPD)</li>
                <li>Legítimo interesse (art. 7º, IX)</li>
                <li>Cumprimento de obrigação legal</li>
                <li>Exercício regular de direitos políticos</li>
                <li>Consentimento, quando aplicável</li>
              </ul>
            </section>

            <section>
              <h3>5. ARMAZENAMENTO E INFRAESTRUTURA</h3>
              <p>A plataforma utiliza:</p>
              <ul>
                <li>Hospedagem: Hostinger</li>
                <li>Banco de dados e autenticação: Supabase</li>
                <li>Armazenamento de arquivos: Supabase Storage</li>
                <li>Pagamentos: Asaas</li>
                <li>Geolocalização: Google Maps</li>
              </ul>
              <p>Os dados podem ser armazenados em servidores localizados no Brasil ou no exterior, conforme infraestrutura dos fornecedores, respeitando padrões adequados de segurança e cláusulas contratuais compatíveis com a LGPD.</p>
            </section>

            <section>
              <h3>6. SEGURANÇA DA INFORMAÇÃO</h3>
              <p>São adotadas medidas como:</p>
              <ul>
                <li>Autenticação segura via Supabase</li>
                <li>Controle de permissões por perfil</li>
                <li>Logs de auditoria</li>
                <li>Criptografia de senha</li>
                <li>Tokens seguros para pagamento</li>
                <li>Controle de acesso baseado em função</li>
              </ul>
            </section>

            <section>
              <h3>7. PRAZO DE RETENÇÃO</h3>
              <p>Os dados permanecerão armazenados enquanto:</p>
              <ul>
                <li>Vigorar o contrato SaaS</li>
                <li>Houver obrigação legal</li>
                <li>For necessário para defesa em processos judiciais</li>
              </ul>
              <p>Após o encerramento do contrato, os dados poderão ser excluídos, anonimizados ou exportados ao controlador.</p>
            </section>

            <section>
              <h3>8. DIREITOS DOS TITULARES</h3>
              <p>Nos termos da LGPD, o titular poderá solicitar:</p>
              <ul>
                <li>Confirmação de tratamento</li>
                <li>Acesso</li>
                <li>Correção</li>
                <li>Exclusão</li>
                <li>Portabilidade</li>
                <li>Revogação de consentimento</li>
              </ul>
              <p>Solicitações devem ser enviadas para: 📩 <strong>agencia@dialetike.org</strong></p>
            </section>

            <section>
              <h3>9. TRANSFERÊNCIA INTERNACIONAL</h3>
              <p>Caso os serviços de infraestrutura utilizem servidores fora do Brasil, a transferência ocorrerá mediante cláusulas contratuais padrão, garantia de nível adequado de proteção e observância às normas da ANPD.</p>
            </section>

            <section>
              <h3>10. ALTERAÇÕES</h3>
              <p>A política poderá ser atualizada a qualquer momento para adequação legal ou técnica.</p>
            </section>
          </div>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPolicy;